import { Injectable } from '@angular/core';
import { RuntimeConfig } from '@app/runtime-config';
import { TabSessionService } from '@app/shared/http-service/tab-session.service';
import { LogEventType, LogException, LogHandler, LogPayload } from '@app/shared/logging/abstract';
import { LogService } from '@app/shared/logging/log.service';
import { Constants } from '@app/shared/models/constants';
import type { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { filter } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class AppInsightsService {

	private appInsights: ApplicationInsights | undefined;

	constructor (
		private logService: LogService,
		private runtimeConfig: RuntimeConfig,
		private tabSessionService: TabSessionService
	) { }

	async init (): Promise<void> {
		if (!this.runtimeConfig.appSettings.logging.appInsights.enabled) return;

		// Dynamic import - SDK only downloaded when AppInsights is actually enabled
		const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');

		this.appInsights = new ApplicationInsights({
			config: {
				connectionString: this.runtimeConfig.appSettings.logging.appInsights.connectionString,
				// Chrome may block unload handlers under Permissions Policy; prefer pagehide/visibilitychange.
				disablePageUnloadEvents: ['unload']
			}
		});

		this.addFailedRequestOnlyInitializer(this.appInsights);
		this.addClientTimeInitializer(this.appInsights);
		this.addTabSessionIdInitializer(this.appInsights);
		this.appInsights.loadAppInsights();
		this.setupListeners();
	}

	private setupListeners (): void {
		const handlers = this.getSupportedHandlers();
		const observable = this.logService.getObservable();
		for (let handler of handlers) {
			observable
				.pipe(filter(f => f.eventType == handler.eventType))
				.subscribe(payload => handler.handler(payload.payload));
		}
	}

	private getSupportedHandlers (): LogHandler[] {
		const handlers: LogHandler[] = [
			{
				eventType: LogEventType.Information,
				handler: this.log.bind(this)
			},
			{
				eventType: LogEventType.Warning,
				handler: this.log.bind(this)
			},
			{
				eventType: LogEventType.Debug,
				handler: this.log.bind(this)
			},
			{
				eventType: LogEventType.Error,
				handler: this.logError.bind(this)
			},
			{
				eventType: LogEventType.Fatal,
				handler: this.logFatal.bind(this)
			}
		];

		return handlers;
	}

	private log (payload: LogPayload): void {
		payload.properties = {
			...(payload.properties ?? {}),
			message: payload.message
		};

		this.appInsights?.trackEvent({
			name: payload.name,
			properties: payload.properties,
			measurements: payload.measurements
		}, {
			message: payload.message
		});
	}

	private logError (payload: LogException): void {
		let error = payload.exception;
		if (error == null) {
			error = new Error(payload.message);
		}

		this.appInsights?.trackException({
			error: error,
			properties: payload.properties,
			measurements: payload.measurements
		});
	}

	private logFatal (payload: LogException): void {
		payload.properties = {
			...(payload.properties ?? {}),
			message: payload.message,
			fatal: true
		};

		this.appInsights?.trackException({
			error: payload.exception,
			properties: payload.properties,
			measurements: payload.measurements
		});
	}

	/**
	 * Drop successful requests from dependency tracking
	 */
	addFailedRequestOnlyInitializer (appInsights: ApplicationInsights) {
		appInsights.addDependencyInitializer((dep) => {
			const item = dep.item;
			if (item === undefined || item === null) {
				return true;
			}

			let failed = dep.aborted === true
				|| item.success === false
				|| item.responseCode < 200
				|| item.responseCode >= 400;

			// Return false to DROP the event
			return failed;
		});
	}

	/**
	 * Add the tab session ID to all telemetry items
	 */
	addTabSessionIdInitializer (appInsights: ApplicationInsights) {
		appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
			// Normalize the location for custom properties
			const baseData: any = item.baseData || item.data?.baseData || item.data || {};
			baseData.properties = baseData.properties || {};

			baseData.properties[Constants.TabSessionHeaderKey] = this.tabSessionService.getSessionId();

			// Write back to the item in the right place
			if (item.baseData) {
				item.baseData = baseData;
			} else if (item.data?.baseData) {
				item.data.baseData = baseData;
			} else if (item.data) {
				item.data = baseData;
			}

			return true;
		});
	}

	/**
	 * Add client time information to all telemetry items
	 */
	addClientTimeInitializer (appInsights: ApplicationInsights) {
		appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
			// Normalize the location for custom properties
			const baseData: any = item.baseData || item.data?.baseData || item.data || {};
			baseData.properties = baseData.properties || {};

			// Capture local time values once per item
			const now = new Date();

			// UTC ISO string
			const utcIso = now.toISOString();

			// Timezone offset in minutes: positive if behind UTC, negative if ahead (JS spec)
			const tzOffsetMinutes = now.getTimezoneOffset();

			// IANA timezone name (best effort; may be undefined in some environments)
			let tzName: string | undefined;
			try {
				tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
			} catch {
				tzName = undefined;
			}

			// Attach properties
			baseData.properties['clientUtcTime'] = utcIso;
			baseData.properties['clientTzOffsetMinutes'] = tzOffsetMinutes;
			if (tzName) baseData.properties['clientTzName'] = tzName;

			// Write back to the item in the right place
			if (item.baseData) {
				item.baseData = baseData;
			} else if (item.data?.baseData) {
				item.data.baseData = baseData;
			} else if (item.data) {
				item.data = baseData;
			}

			return true;
		});
	}
}